import React, { useEffect, useState } from "react";
import { getSelectablePaymentMethods } from "utils/site";
import { StepProps } from "../types";
import { useWebTranslation } from "translations";
import {
  PaymentError,
  PaymentResult,
} from "components/Signup/components/Payment/types";
import useInitMultiBind from "components/Signup/components/Payment/useInitMultiBind";
import useCheckBind from "components/Signup/components/Payment/useCheckBind";
import { trackPurchase } from "components/Signup/analytics";
import useInitStripe from "components/Signup/components/Payment/stripe/useInitStripe";
import useInitTrustly from "components/Signup/components/Payment/trustly/useInitTrustly";
import PaymentView from "components/Signup/components/Payment/PaymentView";
import usePaymentMethodState from "components/Signup/components/Payment/usePaymentMethodState";
import { useSignupDispatch } from "components/Signup/store/hooks";
import { updateInsuranceById } from "components/Signup/store/slices/animals";
import { useMetadata } from "components/Signup/store/selectors/metadata";
import { useAllValidAnimals } from "components/Signup/store/selectors/animals";
import { useDraftId } from "components/Signup/store/selectors/metadata";
import { updateMetadata } from "components/Signup/store/slices/metadata";
import { SelectablePaymentMethodEnum } from "config/site";

const Payment = ({
  next,
  experimentSelectablePaymentMethods,
}: StepProps & {
  experimentSelectablePaymentMethods?: SelectablePaymentMethodEnum[];
}) => {
  const { t } = useWebTranslation();
  const dispatch = useSignupDispatch();
  const selectablePaymentMethods =
    experimentSelectablePaymentMethods ?? getSelectablePaymentMethods();
  const [paymentMethod, setPaymentMethod] = useState(
    selectablePaymentMethods[0]
  );
  const [paymentMethodState, setPaymentMethodState] = usePaymentMethodState();
  const [stripeInitiated, setStripeInitated] = useState(false);
  const [trustlyInitiated, setTrustlyInitated] = useState(false);
  const [paymentMethodInProgress, setPaymentMethodInProgress] =
    useState<boolean>(false);
  const [error, setError] = useState<PaymentError>(null);
  const { fromApp, returnUrl } = useMetadata();
  const draftId = useDraftId();
  const animals = useAllValidAnimals();

  const { pollPolicy, isPollingPolicy, results, status } = useCheckBind({
    onTimeout: () => {
      setError({ message: t("payment.error_timeout_insurance") });
      setStripeInitated(true);
    },
    onPollingDone: () => {
      dispatch(updateMetadata({ loading: false }));
    },
  });

  useEffect(() => {
    if (status === "complete") {
      (async () => {
        await Promise.all(
          results?.map(
            (result) =>
              result.policy &&
              trackPurchase(result.policy, paymentMethod, true, fromApp)
          ) ?? []
        );
        if (returnUrl) {
          // Used by the app
          window.location.href = `${returnUrl}?${results
            ?.map((result) => `policyId=${result.policy?._id}`)
            .join("&")}`;
        } else {
          animals.forEach((animal) =>
            animal.insurances.forEach((insurance) => {
              const newPolicy = results?.find(
                (result) => result?._id === insurance.id
              );
              if (!newPolicy) {
                return;
              }

              dispatch(
                updateInsuranceById(
                  {
                    id: insurance.id,
                    update: {
                      policy: newPolicy.policy,
                    },
                  },
                  { refetch: false }
                )
              );
            })
          );

          next();
        }
      })();
    }
  }, [status]);

  const handleSetupResult = async (result: PaymentResult): Promise<void> => {
    if (result.error) {
      setError(result.error);
      setStripeInitated(true);
      setTrustlyInitated(true);
    } else if (result.redirectUrl) {
      window.location.href = result.redirectUrl;
    } else {
      pollPolicy(draftId ?? "");
    }
  };

  const { stripe, elements } = useInitStripe({
    handleSetupResult,
    handleUrlMatch: (matchPaymentMethod) => {
      if (matchPaymentMethod) {
        setPaymentMethod(matchPaymentMethod);
      } else {
        setStripeInitated(true);
      }
    },
  });

  const initMultiBind = useInitMultiBind({ stripe, elements });

  useInitTrustly({
    handleSetupResult,
    handleUrlMatch: (matchPaymentMethod) => {
      if (matchPaymentMethod) {
        setPaymentMethod(matchPaymentMethod);
      } else {
        setTrustlyInitated(true);
      }
    },
  });

  const onPaymentClick = async () => {
    setError(error ? { message: "" } : null);
    setPaymentMethodInProgress(true);

    const { result, error: bindError } = await initMultiBind({
      draftId,
      paymentMethod,
      paymentData: paymentMethodState.data,
    });

    if (bindError) {
      setError(bindError);
    } else if (result) {
      await handleSetupResult(result);
    }

    setPaymentMethodInProgress(false);
  };

  return (
    <PaymentView
      paymentMethod={paymentMethod}
      setPaymentMethod={setPaymentMethod}
      paymentMethodState={paymentMethodState}
      setPaymentMethodState={setPaymentMethodState}
      loading={paymentMethodInProgress || isPollingPolicy}
      onPaymentClick={onPaymentClick}
      selectablePaymentMethods={selectablePaymentMethods}
      initiated={stripeInitiated && trustlyInitiated}
      error={error}
    />
  );
};

export default Payment;
